import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Eye, EyeOff, KeyRound, Plus, Shield, Trash2, Copy, Pencil } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import VaultLockGate from "@/components/VaultLockGate";

const empty = () => ({ card_id: "", password: "", hint: "", format_note: "" });

export default function StatementPasswords() {
    return (
        <VaultLockGate>
            <StatementPasswordsInner />
        </VaultLockGate>
    );
}

function StatementPasswordsInner() {
    const { hasRole } = useAuth();
    const [rows, setRows] = useState([]);
    const [cards, setCards] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty());
    const [editingCard, setEditingCard] = useState(false);   // true → form is pre-filled for edit
    const [revealed, setRevealed] = useState({});  // card_id -> plaintext
    const [busy, setBusy] = useState(false);

    const load = () => {
        api.get("/statement-passwords").then((r) => setRows(r.data?.items || []));
        api.get("/cards").then((r) => setCards(r.data || []));
    };
    useEffect(() => { load(); }, []);

    const set = (k) => (v) => setForm((s) => ({ ...s, [k]: v }));
    const setInput = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

    const save = async (e) => {
        e?.preventDefault();
        if (!form.card_id) { toast.error("Card is required"); return; }
        if (!editingCard && !form.password) { toast.error("Password required"); return; }
        setBusy(true);
        try {
            // If editing without a new password, only update hint/format via PATCH-style upsert
            if (editingCard && !form.password) {
                await api.patch(`/statement-passwords/${form.card_id}`, {
                    hint: form.hint, format_note: form.format_note,
                });
            } else {
                await api.post("/statement-passwords", form);
            }
            toast.success(editingCard ? "Updated" : "Password saved securely");
            setOpen(false); setEditingCard(false); setForm(empty()); load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to save");
        } finally { setBusy(false); }
    };

    const reveal = async (card_id) => {
        try {
            const r = await api.post(`/statement-passwords/${card_id}/reveal`);
            setRevealed((prev) => ({ ...prev, [card_id]: r.data.password }));
            toast.success("Revealed · audit-logged");
            // Auto-hide after 45s
            setTimeout(() => setRevealed((prev) => {
                const c = { ...prev }; delete c[card_id]; return c;
            }), 45000);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Reveal failed");
        }
    };

    const hide = (card_id) => setRevealed((prev) => {
        const c = { ...prev }; delete c[card_id]; return c;
    });

    const copyToClipboard = async (card_id) => {
        const val = revealed[card_id];
        if (!val) { await reveal(card_id); return; }
        try {
            await navigator.clipboard.writeText(val);
            toast.success("Copied to clipboard");
        } catch {
            toast.error("Copy failed");
        }
    };

    const openEdit = (row) => {
        setEditingCard(true);
        setForm({
            card_id: row.card_id,
            password: "",   // never returned — user re-enters if changing
            hint: row.hint || "",
            format_note: row.format_note || "",
        });
        setOpen(true);
    };

    const openAdd = () => { setEditingCard(false); setForm(empty()); setOpen(true); };

    const remove = async (card_id) => {
        if (!window.confirm("Delete this password entry?")) return;
        try {
            await api.delete(`/statement-passwords/${card_id}`);
            toast.success("Deleted");
            load();
        } catch (err) {
            toast.error("Failed to delete");
        }
    };

    return (
        <div data-testid="statement-passwords-page">
            <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="eyebrow flex items-center gap-1.5"><Shield size={11} /> Statement Password Vault</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                        PDF passwords, secured.
                    </h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                        Store your card statement PDF passwords with encrypted-at-rest storage.
                        Passwords are <span className="text-stone-900 font-medium">masked by default</span> —
                        every reveal is captured in the audit log.
                        <span className="text-red-700 font-medium"> CVV, OTP, PIN and net-banking passwords are never stored anywhere.</span>
                    </p>
                </div>
                {hasRole("admin", "finance") && (
                    <Button onClick={openAdd} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="pwd-add-btn">
                        <Plus size={16} className="mr-1.5" /> Save password
                    </Button>
                )}
            </div>

            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <table className="dense-table w-full">
                    <thead>
                        <tr>
                            <th>Card</th>
                            <th>Password</th>
                            <th>Hint</th>
                            <th>Format</th>
                            <th>Updated</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr><td colSpan={6} className="text-center text-stone-500 py-14">
                                No passwords stored yet. {hasRole("admin", "finance") && "Click \"Save password\" to add one."}
                            </td></tr>
                        )}
                        {rows.map((r) => {
                            const isRevealed = revealed[r.card_id];
                            return (
                            <tr key={r.card_id} data-testid={`pwd-row-${r.card_id}`}>
                                <td>
                                    <div className="text-stone-900 font-medium">{r.bank_name}</div>
                                    <div className="text-xs text-stone-500">{r.card_name} · ••{r.last_4}</div>
                                </td>
                                <td>
                                    <span
                                        className={`inline-flex items-center gap-1 px-2 py-1 rounded border tabular-nums font-mono-figure ${isRevealed ? "text-red-700 border-red-200 bg-red-50 font-medium" : "text-stone-700 border-slate-200 bg-slate-50"}`}
                                        data-testid={`pwd-value-${r.card_id}`}
                                    >
                                        {isRevealed || `${"•".repeat(Math.min(r.password_length || 8, 12))}`}
                                    </span>
                                    <span className="text-[10px] text-stone-400 ml-2">{r.password_length || 0} chars</span>
                                </td>
                                <td className="text-xs text-stone-600">{r.hint || "—"}</td>
                                <td className="text-xs text-stone-600 font-mono-figure">{r.format_note || "—"}</td>
                                <td className="text-xs text-stone-500">
                                    {r.updated_at?.slice(0, 10) || "—"}
                                    {r.updated_by && <div className="text-[10px] text-stone-400">by {r.updated_by}</div>}
                                </td>
                                <td>
                                    {hasRole("admin", "finance") && (
                                        <div className="flex items-center justify-end gap-1">
                                            {isRevealed ? (
                                                <button className="p-1.5 rounded hover:bg-stone-100" onClick={() => hide(r.card_id)} title="Hide" data-testid={`pwd-hide-${r.card_id}`}>
                                                    <EyeOff size={13} />
                                                </button>
                                            ) : (
                                                <button className="p-1.5 rounded hover:bg-blue-50 text-blue-700" onClick={() => reveal(r.card_id)} title="Reveal (audit-logged)" data-testid={`pwd-reveal-${r.card_id}`}>
                                                    <Eye size={13} />
                                                </button>
                                            )}
                                            <button className="p-1.5 rounded hover:bg-stone-100" onClick={() => copyToClipboard(r.card_id)} title="Reveal & copy">
                                                <Copy size={13} />
                                            </button>
                                            <button className="p-1.5 rounded hover:bg-stone-100 text-stone-500 hover:text-stone-900" onClick={() => openEdit(r)} title="Edit hint / replace password" data-testid={`pwd-edit-${r.card_id}`}>
                                                <Pencil size={13} />
                                            </button>
                                            {hasRole("admin") && (
                                                <button className="p-1.5 rounded hover:bg-red-50 text-stone-500 hover:text-red-600" onClick={() => remove(r.card_id)} title="Delete">
                                                    <Trash2 size={13} />
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </td>
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingCard(false); setForm(empty()); } }}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <KeyRound size={16} /> {editingCard ? "Edit statement password" : "Save statement password"}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={save} className="space-y-4">
                        <div>
                            <Label className="eyebrow">Card</Label>
                            <Select value={form.card_id} onValueChange={set("card_id")} disabled={editingCard}>
                                <SelectTrigger data-testid="pwd-form-card"><SelectValue placeholder="Choose a card…" /></SelectTrigger>
                                <SelectContent>
                                    {cards.filter((c) => !c.archived).map((c) => (
                                        <SelectItem key={c.id} value={c.id}>
                                            {c.bank_name} · {c.card_name} · ••{c.last_4}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="eyebrow">
                                {editingCard ? "New password (leave blank to keep existing)" : "Password (encrypted at rest)"}
                            </Label>
                            <Input type="text" value={form.password} onChange={setInput("password")}
                                   required={!editingCard}
                                   placeholder={editingCard ? "leave blank to keep current" : "e.g. RAKE0509"}
                                   className="font-mono-figure" data-testid="pwd-form-password" />
                        </div>
                        <div>
                            <Label className="eyebrow">Hint (safe to display)</Label>
                            <Input value={form.hint} onChange={setInput("hint")} placeholder="e.g. name+dob" />
                        </div>
                        <div>
                            <Label className="eyebrow">Format note</Label>
                            <Input value={form.format_note} onChange={setInput("format_note")} placeholder="e.g. First 4 name + DDMM" />
                        </div>
                        <div className="text-[11px] text-stone-500 border-l-2 border-amber-300 pl-3 py-1">
                            Reminder: we never store CVV, OTP, PIN or net-banking passwords. Only the statement PDF password belongs here.
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => { setOpen(false); setEditingCard(false); }}>Cancel</Button>
                            <Button type="submit" disabled={busy} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="pwd-form-save">
                                {busy ? "Saving…" : editingCard ? "Save changes" : "Save securely"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
